import { generateUrl } from '@/util/imageUtils';
import { isProductConfigurable } from '@/util/isProductConfigurable';

export const getMediaGalleryEntries = (product, optionCodes, optionSelections) => {
  let value = [];

  const { media_gallery_entries, variants } = product || {};
  const isConfigurable = isProductConfigurable(product);

  // Selections are initialized to "code => undefined". Once we select a value, like color, the selections change. This filters out unselected options.
  const optionsSelected = Array.from(optionSelections.values()).filter(value => !!value).length > 0;

  if (!isConfigurable || !optionsSelected) {
    value = media_gallery_entries;
  } else {
    // If any of the possible variants matches the selection add that
    // variant's image to the media gallery. NOTE: This _can_, and does,
    // include variants such as size. If Magento is configured to display
    // an image for a size attribute, it will render that image.
    let item = findMatchedImageVariant({
      optionCodes,
      optionSelections,
      variants,
    });

    // 全选项都匹配不到带图的子sku时，降级为只按颜色匹配，取该颜色下任意一个有图的子sku
    if (!item && hasSelectedOption(optionCodes, optionSelections, COLOR_ATTRIBUTE_CODE)) {
      item = findMatchedImageVariant({
        optionCodes,
        optionSelections,
        variants,
        matchOnlyAttributeCodes: [COLOR_ATTRIBUTE_CODE],
      });
    }

    // 连颜色都匹配不到带图的子sku（即该颜色下所有子sku都没图），回退主sku图
    value = getVariantMediaGalleryEntries(item) || media_gallery_entries;
  }

  return value;
};

// 颜色属性code
const COLOR_ATTRIBUTE_CODE = 'lw_color';

// 匹配子sku图片时忽略的选项，例如size，不同size相同颜色的子sku图相同
const MATCH_IMAGE_VARIANT_IGNORED_ATTRIBUTE_CODE = ['lw_size_shoes_adult'];

// 子sku有图时返回其图集副本，否则返回null
const getVariantMediaGalleryEntries = item => {
  const entries = item?.product?.media_gallery_entries;

  return entries && entries.length ? [...entries] : null;
};

// 当前选择中是否包含指定属性
const hasSelectedOption = (optionCodes, optionSelections, attributeCode) => {
  for (const [id, value] of optionSelections) {
    if (!!value && optionCodes.get(id) === attributeCode) {
      return true;
    }
  }

  return false;
};

// 查找与当前选择匹配且带图的子sku。传入matchOnlyAttributeCodes时只校验这些属性
const findMatchedImageVariant = ({ variants, optionCodes, optionSelections, matchOnlyAttributeCodes }) => {
  if (!variants) {
    return null;
  }

  return variants?.find(({ attributes, product }) => {
    const customAttributes = (attributes || []).reduce(
      (map, { code, value_index }) => new Map(map).set(code, value_index),
      new Map(),
    );

    for (const [id, value] of optionSelections) {
      const code = optionCodes.get(id);

      if (matchOnlyAttributeCodes && !matchOnlyAttributeCodes.includes(code)) {
        continue;
      }

      const matchesStandardAttribute = product[code] === value;
      const matchesCustomAttribute = customAttributes.get(code) === value;

      if (
        !matchesStandardAttribute &&
        !matchesCustomAttribute &&
        !MATCH_IMAGE_VARIANT_IGNORED_ATTRIBUTE_CODE.includes(code)
      ) {
        return false;
      }
    }

    return !!getVariantMediaGalleryEntries({ product });
  });
};

// 获得加车单张图片
export const getSingleImage = mediaGalleryEntries => {
  let media = mediaGalleryEntries && mediaGalleryEntries[0];

  // 默认展示图片position最小的
  mediaGalleryEntries &&
    mediaGalleryEntries.forEach((item, index) => {
      if (item.position < media.position) {
        media = item;
      }
    });

  const product_image =
    process.env.NEXT_PUBLIC_MAGENTO_BACKEND_URL +
    generateUrl(mediaGalleryEntries && media?.file, 'image-product')(90, 132);

  return product_image;
};
